import React, { useMemo, useState } from 'react'

import { Box, NotFound } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import {
  ModuleId,
  OneModal,
  ResourceHeader,
} from '@factorialco/factorial-one-react/dist/experimental'
import { ChevronLeft, ChevronRight } from '@factorialco/factorial-one-react/icons/app'
import { FormFormEditorDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { formFromData } from '../hooks/useForms'
import { Element } from '../lib/Element'
import { SectionElement } from '../lib/elements/SectionElement'
import { ElementPreview } from './preview/ElementPreview'

interface FormPreviewProps {
  formId: string
  resourceType?: string
  resourceId?: string // TODO NOT USED YED
  isOpen: boolean
  onClose: () => void
  resourceHeaderModule?: {
    id: ModuleId
    label: string
    href: string
  }
}

function isSectionElement(el: Element | SectionElement): el is SectionElement {
  return 'elements' in el && Array.isArray(el.elements)
}

function flattenElements(elements: Element[] | SectionElement[]): Element[] {
  const flat: Element[] = []

  for (const el of elements) {
    if (isSectionElement(el)) {
      flat.push(el)
      flat.push(...flattenElements(el.elements))
    } else {
      flat.push(el)
    }
  }

  return flat
}

const FormPreview: React.FC<FormPreviewProps> = ({
  formId,
  resourceType,
  isOpen,
  onClose,
  resourceHeaderModule,
}) => {
  const { data: backendData, loading: loadingForm } = useQuery(FormFormEditorDocument, {
    variables: {
      id: formId ?? '',
      // resourceId: resourceId,
      resourceType: resourceType ?? '',
    },
    skip: !formId && !resourceType,
  })

  const formData = backendData?.forms.form
  const form =
    formData &&
    formFromData({
      ...formData,
      description: formData.description ?? '',
      elements: formData.elements.map((el) => ({ ...el, description: el.description ?? '' })),
    })

  const [currentElementIndex, setCurrentElementIndex] = useState<number>(0)
  const elements = form ? flattenElements(form?.elements) : []

  const currentElement = useMemo(() => {
    return elements[currentElementIndex]
  }, [currentElementIndex, form?.elements])

  if (!form) return <NotFound title="" description="" />
  return (
    <OneModal onClose={onClose} isOpen={isOpen} position="right">
      <OneModal.Header module={resourceHeaderModule} />
      <OneModal.Content>
        <Box paddingX="s16">
          <ResourceHeader
            title={form.title}
            metadata={
              // we read 'formType' and 'status' fields from 'formData' because these fields aren't being managed in 'Form' class
              formData?.status === 'active'
                ? [
                    formData.formType === 'satisfaction' && {
                      label: 'Visibility',
                      value: {
                        type: 'text',
                        content: formData.anonymous ? 'Anonymous' : 'Visible',
                      },
                    },
                  ]
                : []
            }
            status={{
              label: '',
              text:
                formData?.status === 'active'
                  ? i18n.t('trainings.surveys.status.sent')
                  : i18n.t('trainings.surveys.status.draft'),
              variant: formData?.status === 'active' ? 'positive' : 'neutral',
            }}
          />

          {currentElement && <ElementPreview element={currentElement} form={form} />}
        </Box>
      </OneModal.Content>
      <OneModal.Footer>
        <Box flexDirection="row" justifyContent="spaceBetween" width="full" gap="s8">
          <Box gap="s8" flexDirection="row">
            <Button
              icon={ChevronLeft}
              hideLabel
              onClick={() => setCurrentElementIndex((prev) => prev - 1)}
              disabled={currentElementIndex === 0}
            />
            <Button
              icon={ChevronRight}
              hideLabel
              onClick={() => setCurrentElementIndex((prev) => prev + 1)}
              disabled={currentElementIndex === elements.length - 1}
            />
          </Box>
          <Button label={'Submit'} disabled />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}

export default FormPreview
